// Page guides — a step-by-step walkthrough, one page at a time.
//
// This replaced a side panel that listed everything a page could do and left
// the reader to match words to controls themselves. A guide is now a small
// modal that points at ONE thing, waits where the reader has to act, and moves
// on when they have. Design: "Page Guides — Step Modals".
//
// Three rules the shape below exists to keep:
//
//  1. A step names its anchor by `data-testid`, never by coordinates. The
//     dashboard requires a testid on every interactive element
//     (apps/dashboard/CLAUDE.md rule 4), so the anchor is a contract the repo
//     enforces rather than a guess about layout.
//  2. A step that asks the reader to DO something does not offer Next. It
//     watches the page and advances itself when the thing is done. Claiming a
//     step is finished is the page's job, not the reader's.
//  3. A step that cannot apply right now is dropped before the guide opens, so
//     "Step 2 of 3" counts only steps this reader will actually see.

import type { SystemRole } from "./rbac";

/**
 * Something the guide can observe on the page.
 *
 * A CSS selector rather than a bare testid because the interesting questions
 * are "is there a connected badge anywhere" and "is any row still expired",
 * which are suffix and attribute matches. `text` narrows to elements whose
 * text contains it, case-insensitively — the only way to read a status that is
 * rendered as a word rather than an attribute.
 */
export interface GuideSignal {
  selector: string;
  text?: string;
  /**
   * "present" fires once something matches; "absent" once nothing does.
   *
   * "increase" fires when MORE things match than when the step opened, and is
   * what a step asking the reader to create something needs. "present" is
   * already true for anyone who has one -- an org with a connection saw the
   * connect step show "Done." and skip itself 1.2s later, logged as did_it
   * for an action nobody performed, which is the one number the analytics
   * exists to get right.
   */
  mode: "present" | "absent" | "increase" | "decrease";
  /**
   * Re-sample the baseline whenever the number of things matching THIS
   * selector changes, so a filter cannot be mistaken for the reader acting.
   *
   * Connectors needs it. "Did they connect something" is a page-wide question
   * -- the instruction is "press Connect on the app you want", any app -- but
   * a page-wide count is also moved by the search box the step before tells
   * them to use: measured on dev it read 1 filtered to "slack", 2 cleared and
   * 0 on "stripe". Scoping to the ringed card killed the filter problem and
   * created a worse one, that connecting any other card never completed the
   * step. What separates the two is that filtering changes how many CARDS are
   * on screen and connecting does not, which is exactly what this watches.
   */
  rebaseOn?: string;
}

export interface GuideStep {
  /** Stable slug. Keys the test ids and the stored position. */
  id: string;
  title: string;
  body: string;
  /**
   * The control this step is about, as a `data-testid`. A trailing `-` means a
   * prefix: the guide rings the first match, which is how a step points at "the
   * first row" without knowing any ids.
   *
   * A missing anchor does not hide the step. The modal moves to the middle of
   * the page and the words carry it — the old panel hid itself instead, and a
   * guide that disappears is worse than one pointing nowhere.
   */
  anchor?: string | string[];
  /**
   * Narrows a prefix anchor to the match whose text contains this, so a step
   * about one row in a list rings that row rather than the first one.
   *
   * Without it "fix the expired one" rang row 0: on dev that is an Active
   * connection and the first Expired row is 62 rows below it, off-screen.
   */
  anchorText?: string;
  /**
   * The reader has to do this one. No Next: the guide watches `done` and moves
   * on by itself, which also works while it is minimised.
   */
  wait?: { instruction: string; done: GuideSignal };
  /**
   * Work the agent can do instead, as the message to hand it.
   *
   * Only where that is true. Telling someone "or ask the agent to" and leaving
   * them to find it is not guidance; this puts a button on the step that opens
   * the agent with the request already typed, unsent, so they can edit it.
   * The step keeps waiting either way — it watches the page, not who acted.
   */
  agentPrompt?: string;
  /**
   * Fills `{name}` placeholders in `agentPrompt` from the page when the button
   * is pressed: an input's value, or an element's text. `fallback` when the
   * page does not have it. "Change my workflow" with a workflow open in front
   * of the reader handed the agent a request it had to ask about.
   */
  agentPromptFill?: Record<string, { selector: string; fallback: string }>;
  /**
   * Presses inside the anchor that do not count as acting on it, for controls
   * nested in a row that do something else (a row's copy-id button).
   */
  actIgnore?: string;
  /** Dropped from the guide when this is false as the guide opens. */
  onlyIf?: GuideSignal;
  /**
   * Passed over, at the moment the reader would land on it, when the page
   * does not meet this ("present" or "absent"). Unlike `onlyIf` it is read on
   * every move, for steps that depend on an earlier one having been done:
   * skip "Open one" and the steps inside the editor go with it.
   */
  // Only the modes that ask about the page as it is: "increase" and
  // "decrease" compare against a baseline a skip check never has, so they
  // would pass the step over every time.
  requires?: GuideSignal & { mode: "present" | "absent" };
  /**
   * Work out the remaining steps again when the reader presses Next here.
   * For a step that invites changing what the page shows (the Connections
   * view switch): the rest of the guide is about whatever they changed it to.
   * Only on Next from this step -- anywhere else, renumbering under the
   * reader is the thing the settle is written not to do.
   */
  resettle?: boolean;
  /**
   * Show this step again, on its own, if its `onlyIf` was false while the
   * reader walked the guide and comes true later. For situations that arrive
   * after onboarding -- nobody has an expired connection on day one -- so the
   * advice reaches them when it is about something on their screen. Once per
   * step, and only for readers who started the guide.
   */
  resurface?: boolean;
  /** Label for the forward button on the LAST step. Default "Done". */
  lastLabel?: string;
  /** Where the last button goes, when it hands off to another page. */
  to?: string;
}

export interface PageGuide {
  id: string;
  /** The page name, exactly as the sidebar says it. */
  title: string;
  /** One line under "New to X?" on the opening modal. */
  intro: string;
  steps: GuideStep[];
  /**
   * What the opening modal sits under, as a `data-testid`.
   *
   * Omitted means the page header (`.ph`), which every standard page renders.
   * The builders opt out of that header, so they have to name their own title
   * or the opener drifts to a corner.
   */
  introAnchor?: string;
  /** Omitted = every role that can open the page. */
  roles?: SystemRole[];
  /**
   * What the page renders once its data has arrived, as a CSS selector.
   *
   * A resumed guide works out its steps when the page can answer their
   * conditions. Without this it waits for a "present" condition to come true,
   * and on a page where none ever does -- Connections with nothing connected
   * -- it waited out the whole deadline showing every step unfiltered.
   */
  readyWhen?: string;
}

// The Connections table's rows themselves: not their cells and buttons
// (`connections-row-<id>-status-cell`, ...), and not the page's
// `connections-row-error-banner`, which a bare prefix also matched.
const CONNECTION_ROWS =
  '[data-testid^="connections-row-"]:not([data-testid^="connections-row-"] *):not([data-testid="connections-row-error-banner"])';

const BUILDERS: SystemRole[] = ["platform_admin", "owner", "admin", "developer", "operator"];

export const PAGE_GUIDES: Record<string, PageGuide> = {
  connectors: {
    id: "connectors",
    title: "Connectors",
    intro: "Every app fastn can talk to. Here is how to connect yours.",
    roles: BUILDERS,
    steps: [
      {
        id: "search-for-your-app",
        title: "Search for your app",
        body: "There are hundreds of apps here. Typing the name is faster than scrolling.",
        anchor: "connectors-search-input",
      },
      {
        id: "press-connect-and-sign-in",
        title: "Press Connect and sign in",
        body: "It says Connected when it works. This signs in your own account, not your customers'.",
        anchor: "connectors-card-",
        wait: {
          instruction: "Press Connect on the app you want.",
          // Scoped to the card this step rings, so it asks "is THIS one
          // connected" rather than counting badges across the page. The
          // page-wide count was moved by the search box alone -- measured on
          // dev it read 1 filtered to "slack", 2 with the filter cleared and 0
          // on "stripe" -- which both advanced the step for readers who had
          // connected nothing and stranded readers whose app was already
          // connected, where a second account adds no new badge and "increase"
          // could never be satisfied.
          done: {
            selector: '[data-testid$="-connected-badge"]',
            mode: "increase",
            // The cards themselves, not everything inside them: a card's own
            // children are all `connectors-card-<id>-<something>`, so a bare
            // prefix counts badges and buttons too and re-baselines every
            // time a card's own contents change -- including the moment the
            // Connected badge appears, which is the one event that must NOT
            // move the baseline. The :not() keeps only elements that are not
            // inside another card.
            rebaseOn:
              '[data-testid^="connectors-card-"]:not([data-testid^="connectors-card-"] *)',
          },
        },
      },
      {
        id: "not-there-ask-us-for-it",
        title: "Not there? Ask us for it",
        body: "Tell us which app you need and we build the connector for you.",
        anchor: "connectors-request-connector-button",
      },
      {
        id: "manage-it-later-in-connections",
        title: "Manage it later in Connections",
        body: "Sign in with a different account, or disconnect, from there.",
        anchor: "sidebar-connections-link",
        lastLabel: "Take me there",
        to: "/connections",
      },
    ],
  },

  connections: {
    id: "connections",
    title: "Connections",
    intro: "Every account your workflows sign in with, and whether it still works.",
    roles: BUILDERS,
    // The table renders only with rows and the empty state only without, so
    // one of them means the list has arrived.
    readyWhen: '[data-testid="connections-table"], [data-testid="connections-empty-state"]',
    steps: [
      {
        // First, because every step after it is about the list this switch
        // decides. It also replaces a step that only explained the empty
        // Customers view: the difference is worth knowing either way.
        id: "yours-or-your-customers",
        title: "Two kinds of connections",
        body: "My organization is the accounts your team signs in with for your own workflows. Customers is the accounts your customers connect through the widget you embed. You can see theirs here, but only they can add them.",
        anchor: "connections-view-tabs-container",
        // The reader may switch views while reading this, and the steps after
        // it depend on which one they are looking at.
        resettle: true,
        // The switch only renders where the org has a customer view.
        onlyIf: { selector: '[data-testid="connections-view-tabs-container"]', mode: "present" },
      },
      {
        // Only on a page with none yet: the steps below are about rows, and
        // with an empty list they pointed at a table that was not there.
        id: "connect-your-first-account",
        title: "Connect your first account",
        body: "Sign in to an app your workflows will use. It shows up here as a row you can check on later.",
        // The button lives in the empty state and the header, and only in the
        // "My organization" view: in Customers, customers connect their own
        // through the widget. From there the step rings the way back to it,
        // and pressing that is not the action.
        anchor: ["connections-empty-connect-button", "connections-connect-button", "connections-view-mine"],
        actIgnore: '[data-testid="connections-view-mine"]',
        // The empty state of "My organization" only: an org with connections
        // of its own must not be told to connect a first one because its
        // customers have none.
        onlyIf: {
          selector: '[data-testid="connections-empty-state"]',
          text: "No connections yet",
          mode: "present",
        },
        wait: {
          instruction: "Press Connect a system and sign in.",
          // Re-counted whenever customer rows come or go (only the Customers
          // view has a Customer cell): switching to it brought in their rows,
          // which completed the step and logged did_it with nothing
          // connected. A connection of this org's own adds a row without one,
          // so it still counts.
          done: {
            selector: CONNECTION_ROWS,
            mode: "increase",
            rebaseOn: '[data-testid$="-customer-cell"][data-testid^="connections-row-"]',
          },
        },
      },
      {
        id: "one-row-per-account",
        title: "One row per connection",
        body: "Your workflows act through these accounts. If one stops working, so do they.",
        anchor: "connections-table",
        // `requires`, not `onlyIf`: the view switch above the table changes
        // which rows exist while the guide is open.
        requires: { selector: CONNECTION_ROWS, mode: "present" },
      },
      {
        id: "check-the-status",
        title: "Check the status",
        body: "Active means it works. Expired means the sign-in ran out and workflows using it will stop.",
        anchor: "connections-row-",
        requires: { selector: CONNECTION_ROWS, mode: "present" },
      },
      {
        id: "fix-the-expired-one",
        title: "Fix the expired one",
        body: "Reconnecting signs in again. Workflows pick it up straight away.",
        anchor: "connections-row-",
        // Without this the highlight lands on row 0: on dev that is an Active
        // connection and the first Expired row is 62 rows below it, off-screen,
        // while the step says to press Reconnect on the one that expired.
        anchorText: "expired",
        onlyIf: { selector: '[data-testid$="-status-cell"]', text: "expired", mode: "present" },
        // Nobody has an expired connection their first time here.
        resurface: true,
        wait: {
          instruction: "Press Reconnect on the account that expired.",
          // "decrease", not "absent": the instruction is singular -- reconnect
          // the one that expired -- but the count is document-wide, so
          // "absent" asked a reader with 62 expired rows on dev to fix every
          // one of them before the step would move. One fewer than when the
          // step opened is the reader having done what it said.
          done: {
            selector: '[data-testid$="-status-cell"]',
            text: "expired",
            mode: "decrease",
          },
        },
      },
      {
        id: "add-another-account",
        title: "Add another account",
        body: "Sign in to a new app, or a second account on the same one.",
        // The Customers view has no New connection button; ring the switch
        // back to the view that does.
        anchor: ["connections-connect-button", "connections-view-mine"],
        // The first-account step already says this on an empty page.
        onlyIf: { selector: CONNECTION_ROWS, mode: "present" },
      },
    ],
  },

  workflows: {
    id: "workflows",
    title: "Workflows",
    intro: "Everything that moves your data, and whether it is working.",
    roles: ["platform_admin", "owner", "admin", "developer"],
    // Every condition here asks for a workflow row, so on an empty tab the
    // page could never show it had loaded, and a resumed guide or Help sat
    // blank for the whole four-second deadline.
    readyWhen: '[data-testid^="workflows-row-"], [data-testid="workflows-empty-state"]',
    // "Open one" leads, and everything after it happens inside the editor it
    // opens. It used to come last: the editor is full-screen and does not
    // close by itself, and the steps after it pointed at the list underneath,
    // so a reader who did step 1 was shown "Check how its last runs went"
    // with no rows on screen. Moving it last fixed that and broke the reading
    // order instead -- step 1 said "its" before anything was open. Now the
    // later steps point at the editor (anchors that are covered fall through
    // to the next one), so the natural order works.
    steps: [
      {
        id: "open-one-to-see-what-it-does",
        title: "Open one to see what it does",
        body: "Which apps it touches and in what order. The next steps happen inside it.",
        anchor: "workflows-row-",
        // The id under each name copies it rather than opening the row; a
        // press there showed Done for a workflow that never opened.
        actIgnore: '[data-testid$="-workflow-id"]',
        onlyIf: { selector: '[data-testid^="workflows-row-"]', mode: "present" },
        wait: {
          instruction: "Open one of your workflows.",
          // Opening a row calls openEdit, which mounts the code editor. The
          // workflow-detail* testid belongs to the Execute button's view, so
          // a reader doing exactly what this step says never satisfied it and
          // had to press Skip under a note promising it would move on itself.
          // Either half of the editor pane counts as "it opened". The code
          // column only renders where code editing is enabled, which is the
          // parent organisation and almost nowhere else; every other workspace
          // gets codeEditorHiddenNote in its place, so matching the editor
          // alone left the step unsatisfiable for nearly every reader -- the
          // same trap the fix above it was written to escape.
          done: {
            selector:
              '[data-testid="workflows-code-editor"], [data-testid="workflow-code-editor-hidden-note"]',
            mode: "present",
          },
        },
      },
      {
        id: "test-it-before-it-goes-live",
        title: "Test it before it goes live",
        body: "Run it with sample input and check the output before your customers rely on it.",
        // The button only renders on the Test tab; on any other tab, the tab.
        // A workflow can open on another tab, which left a reading step saying
        // "run it" while ringing a tab -- so it asks for the run and waits.
        anchor: ["workflows-test-run-button", "workflows-right-tab-test-button"],
        // Opening the tab is the way to the button, not the run itself; as
        // "acted" it offered Done before anything had run.
        actIgnore: '[data-testid="workflows-right-tab-test-button"]',
        wait: {
          instruction: "Open the Test tab and run it, or check its last result.",
          // Done once a result is on screen, a new run's or an earlier one's:
          // the step is about seeing what a test shows. Requiring a fresh run
          // (counted from a press on Run) was tried in review and taken out --
          // opening the Test tab onto a result stopped moving the guide on,
          // which is the thing a waiting step is for.
          done: { selector: '[data-testid^="workflows-test-result-tab-"]', mode: "present" },
        },
        onlyIf: { selector: '[data-testid^="workflows-row-"]', mode: "present" },
        requires: { selector: '[data-testid="workflow-editor-overlay"]', mode: "present" },
      },
      {
        id: "check-how-its-last-runs-went",
        title: "Check how its last runs went",
        body: "You can see whether each run worked, what it changed, and how long it took.",
        // The editor's run history: the Executions tab, or the More menu it
        // collapses into on a narrow screen.
        anchor: ["workflows-right-tab-history-button", "workflow-editor-right-tabs-more"],
        onlyIf: { selector: '[data-testid^="workflows-row-"]', mode: "present" },
        requires: { selector: '[data-testid="workflow-editor-overlay"]', mode: "present" },
      },
      {
        id: "ask-the-agent-for-any-change",
        title: "Ask the agent for any change",
        body: "\"Run it every hour.\" \"Put it back the way it was last week.\" It writes the change and tests it.",
        anchor: ["sidebar-build-with-ai-link", "topnav-connect-mcp-button"],
        agentPrompt: "Change {workflow} so it runs every hour, and test it before you save it.",
        agentPromptFill: {
          workflow: {
            selector: '[data-testid="workflows-form-name-input"]',
            fallback: "my workflow",
          },
        },
      },
    ],
  },

  triggers: {
    id: "triggers",
    title: "Triggers",
    intro: "What makes your workflows run without you.",
    roles: BUILDERS,
    // Each sub-tab renders its rows or its empty state once its list arrives.
    readyWhen:
      '[data-testid^="triggers-webhook-row-"], [data-testid^="triggers-scheduler-row-"], [data-testid^="triggers-app-row-"], ' +
      '[data-testid$="-empty-state"][data-testid^="triggers-"]',
    steps: [
      {
        id: "three-ways-a-workflow-starts",
        title: "Three ways a workflow starts",
        body: "A webhook when another system calls you, a schedule at a time you pick, or an app event like a new order.",
        anchor: ["triggers-sub-tabs", "triggers-webhook-sub-tab"],
      },
      {
        // Informational, not a waiting step. It used to be conditional on
        // `[data-testid^="triggers-"][data-testid$="-status-cell"]`, which
        // matches nothing: the app's only -status-cell is
        // connections-row-<id>-status-cell. The condition was never true, so
        // the step never appeared, even with a trigger switched off. The
        // Triggers page exposes no per-row status in the DOM to wait on, so
        // the advice stands on its own rather than on a signal that cannot
        // be read. It does need a row to point at: on an empty page it rang
        // nothing. `requires`, because the sub-tabs change which rows exist.
        id: "make-sure-it-is-switched-on",
        title: "Check it is switched on",
        body: "A trigger that is off never starts its workflow. Each row's menu has the toggle.",
        anchor: ["triggers-webhook-row-", "triggers-scheduler-row-", "triggers-app-row-"],
        requires: {
          selector:
            '[data-testid^="triggers-webhook-row-"], [data-testid^="triggers-scheduler-row-"], [data-testid^="triggers-app-row-"]',
          mode: "present",
        },
      },
      {
        id: "add-another-trigger",
        title: "Or ask the agent to add one",
        body: "Describe it instead of filling in the form: start this when a Shopify order is paid, or run it every morning at 8.",
        anchor: ["sidebar-build-with-ai-link", "triggers-add-button"],
        // Ask the agent opens it with this typed in. It replaced "Take me
        // there", which went to the same place with an empty box. A schedule
        // rather than an app event: every org can have one, and naming an app
        // (it said Shopify) handed most readers a request about an app they
        // do not use.
        agentPrompt: "Add a schedule that runs one of my workflows every weekday at 8am. Ask me which one.",
      },
    ],
  },

  widgets: {
    id: "widgets",
    title: "Widgets",
    intro: "The panel your customers use to connect their own apps, inside your product.",
    introAnchor: "widgets-builder-draft-badge",
    roles: BUILDERS,
    steps: [
      {
        id: "this-is-what-your-customers-see",
        title: "This is what your customers see",
        body: "The preview follows every change you make, exactly as it will look inside your app.",
        anchor: "widgets-hub-preview",
      },
      {
        id: "pick-the-apps-it-offers",
        title: "Pick the apps it offers",
        body: "Add only the apps you want your customers to see.",
        anchor: ["widgets-create-btn", "widgets-integrations-toggle"],
        agentPrompt: "Add Shopify and Slack to my widget.",
        wait: {
          instruction: "Press Add and pick an app.",
          // The integration rows in the builder's rail, which is what Add
          // creates. It used to watch `widget-edit-connector-*`, the chips of
          // the EDIT modal: Add opens the create modal, which renders none, so
          // a reader who did exactly this never completed the step. Only rows
          // that are not inside another row or a collapsed version group, so
          // a row's own buttons and expanding a group do not count as adding.
          // "increase" because most builders already have some.
          done: {
            selector:
              '[data-testid^="widget-card-"]:not([data-testid^="widget-card-"] *):not([data-testid^="widgets-rail-versions-"] *)',
            mode: "increase",
          },
        },
      },
      {
        id: "put-it-in-your-product",
        title: "Put it in your product",
        body: "The Embed tab holds the iframe and the SDK snippet. Copy one into your app.",
        anchor: "widgets-tab-embed",
        wait: {
          instruction: "Open the Embed tab.",
          done: { selector: '[data-testid="embed-methods-panel"]', mode: "present" },
        },
      },
      {
        id: "publish-when-it-looks-right",
        title: "Publish when it looks right",
        body: "Your customers keep seeing the live version until you publish.",
        anchor: "widgets-save-btn",
      },
    ],
  },

  customers: {
    id: "customers",
    title: "Customers",
    intro: "The companies using your integrations, and how each one is set up.",
    roles: BUILDERS,
    readyWhen: '[data-testid="customers-table"], [data-testid="customers-empty-state"]',
    steps: [
      {
        id: "who-your-customers-are",
        title: "Who your customers are",
        body: "A customer is one company using your product. Each gets its own connections, kept apart from everyone else.",
        // The empty state when there are none: what a customer is matters
        // before the first one exists, and the table is not there to ring.
        anchor: ["customers-table", "customers-empty-state"],
      },
      {
        id: "create-a-test-customer",
        title: "Create a test customer",
        body: "Try the whole experience as one of your customers before a real one arrives.",
        anchor: "customers-create-button",
        onlyIf: { selector: '[data-testid^="customers-row-"]', mode: "absent" },
        wait: {
          instruction: "Press Create and give it any name.",
          done: { selector: '[data-testid^="customers-row-"]', mode: "present" },
        },
      },
      {
        id: "watch-each-ones-setup",
        title: "Watch each one's setup",
        body: "See which integrations they turned on and which apps they still need to connect.",
        anchor: "customers-row-",
        // About a row; with none yet it rang nothing. `requires`, so it
        // follows on from "Create a test customer" once that one exists.
        requires: { selector: '[data-testid="customers-table"]', mode: "present" },
      },
    ],
  },
};

/** Which guide a route shows, and the tab it needs when the page has tabs. */
const GUIDE_ROUTES: Array<{ path: string; tab?: string; guide: PageGuide }> = [
  { path: "/integrations", tab: "connectors", guide: PAGE_GUIDES.connectors },
  { path: "/connections", guide: PAGE_GUIDES.connections },
  { path: "/integrations", tab: "workflows", guide: PAGE_GUIDES.workflows },
  { path: "/integrations", tab: "triggers", guide: PAGE_GUIDES.triggers },
  { path: "/widgets", guide: PAGE_GUIDES.widgets },
  { path: "/widgets/:widgetId", guide: PAGE_GUIDES.widgets },
  { path: "/customers", guide: PAGE_GUIDES.customers },
];

/** Tab-bearing pages that render a tab when the URL names none. */
const DEFAULT_TAB: Record<string, string> = { "/integrations": "connectors" };

function pathMatches(pattern: string, pathname: string): boolean {
  if (pattern === pathname) return true;
  const p = pattern.split("/");
  const a = pathname.split("/");
  if (p.length !== a.length) return false;
  return p.every((seg, i) => (seg.startsWith(":") ? a[i].length > 0 : seg === a[i]));
}

/**
 * The guide for this location, or null.
 *
 * Null for the Agent: a conversation is its own guide, and a modal pointing at
 * a chat box while someone is mid-sentence is in the way rather than helpful.
 * Null, too, for any page the design does not cover — a page with no
 * walkthrough shows nothing at all, not an empty one.
 */
export function guideForRoute(
  pathname: string,
  search: string,
  role?: SystemRole,
): PageGuide | null {
  const clean = pathname.replace(/\/$/, "") || "/";
  const tab = new URLSearchParams(search).get("tab") ?? DEFAULT_TAB[clean];

  const route = GUIDE_ROUTES.find(
    (r) => pathMatches(r.path, clean) && (!r.tab || r.tab === tab),
  );
  if (!route) return null;
  if (route.guide.roles && !(role && route.guide.roles.includes(role))) return null;
  return route.guide;
}
